import React from 'react';
import { Grid, Stack, Typography, useMediaQuery } from '@mui/material';

// MESSAGES
import HomeFormattedMessages from '../../messages/HomeFormattedMessages';

// Components
import FullscreenLayout from '../../components/layouts/FullscreenLayout';
import Announcements from '../../components/dashboard/cards/Announcements';
import MessageBalance from '../../components/dashboard/cards/MessageBalance';
import UserSmsTraffic from '../../components/dashboard/cards/tables/UserSmsTraffic';
import UserTopErrors from '../../components/dashboard/cards/tables/UserTopErrors';
import CustomerSmsTraffic from '../../components/dashboard/cards/tables/CustomerSmsTraffic';
import CustomerTopErrors from '../../components/dashboard/cards/tables/CustomerTopErrors';
import TopFiveUsers from '../../components/dashboard/cards/tables/TopFiveUsers';
import Invoices from '../../components/dashboard/cards/tables/Invoices';
import Campaigns from '../../components/dashboard/cards/tables/Campaigns';
import MessageFailedDeliveryRoutes from '../../components/dashboard/cards/charts/MessageFailedDeliveryRoutes';
import MessageDeliveryRates from '../../components/dashboard/cards/charts/MessageDeliveryRates';
import ErrorsByCode from '../../components/dashboard/cards/charts/ErrorsByCode';
import MessageLatency from '../../components/dashboard/cards/charts/MessageLatencyAverage';
import ErrorsByTopCustomer from '../../components/dashboard/cards/charts/ErrorsByTopCustomer';

export default function Dashboard({
  navigate,
  accessLevel,
  accessType,
  user,
  customer,
  acting_names,
  dedicatedNumbers,
  errorHandler,
}) {
  const mdAndUp = useMediaQuery((theme) => theme.breakpoints.up('md'));

  return (
    <FullscreenLayout>
      <Stack
        sx={{
          flexDirection: { md: 'row' },
          justifyContent: 'space-between',
          width: '100%',
          display: { xs: 'none', md: 'flex' },
        }}
      >
        <Typography variant="h1" gutterBottom>
          {acting_names?.customer_name}
        </Typography>

        {dedicatedNumbers && dedicatedNumbers.length > 0 && (
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: 'primary.main',
              alignSelf: { sm: 'end' },
              justifySelf: { sm: 'end' },
              maxWidth: { md: '50%' },
              textAlign: { md: 'right' },
            }}
            gutterBottom
          >
            {HomeFormattedMessages.dedicatedNumbers}: {dedicatedNumbers}
          </Typography>
        )}
      </Stack>

      <Grid container sx={{ width: '100%' }} spacing={{ xs: 2, md: 4 }}>
        <Grid size={{ xs: 12, md: 8 }} display="flex">
          <Announcements errorHandler={errorHandler} />
        </Grid>

        {/** Mobile user heading */}
        <Grid size={12} display={{ xs: 'flex', md: 'none' }}>
          <Typography variant="h1" fontSize={20} sx={{ marginTop: 2 }}>
            {user.user_name}
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }} display="flex">
          <MessageBalance
            messageOnly={customer.customer_type !== 'B' || !mdAndUp}
            errorHandler={errorHandler}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} display="flex">
          <UserSmsTraffic
            navigate={navigate}
            mobileView={!mdAndUp}
            errorHandler={errorHandler}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }} display="flex">
          <UserTopErrors
            navigate={navigate}
            mobileView={!mdAndUp}
            errorHandler={errorHandler}
          />
        </Grid>

        {accessLevel > '0' && accessType !== '0' && (
          <>
            {/** Mobile customer heading */}
            <Grid size={12} display={{ xs: 'flex', md: 'none' }}>
              <Typography variant="h1" fontSize={20} sx={{ marginTop: 2 }}>
                {acting_names?.customer_name}
              </Typography>
            </Grid>

            {!mdAndUp && (
              <Grid size={{ xs: 12, md: 4 }} display="flex">
                <MessageBalance
                  creditsOnly={true}
                  errorHandler={errorHandler}
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }} display="flex">
              <CustomerSmsTraffic
                navigate={navigate}
                activeCustomerName={acting_names?.customer_name}
                mobileView={!mdAndUp}
                errorHandler={errorHandler}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} display="flex">
              <CustomerTopErrors
                navigate={navigate}
                activeCustomerName={acting_names?.customer_name}
                mobileView={!mdAndUp}
                errorHandler={errorHandler}
              />
            </Grid>

            <Grid size={{ xs: 12 }} display="flex">
              <TopFiveUsers
                navigate={navigate}
                mobileView={!mdAndUp}
                errorHandler={errorHandler}
              />
            </Grid>

            {/** Use useMediaQuery to avoid unnecessary data loading */}
            {mdAndUp && (
              <>
                <Grid size={{ xs: 12, md: 4 }} display="flex">
                  <Invoices
                    navigate={navigate}
                    customer={acting_names}
                    errorHandler={errorHandler}
                  />
                </Grid>
                {((accessLevel === '3' && accessType === '3') ||
                  customer.hasCampaign) && (
                  <Grid size={{ xs: 12, md: 8 }} display="flex">
                    <Campaigns
                      navigate={navigate}
                      errorHandler={errorHandler}
                    />
                  </Grid>
                )}
              </>
            )}
          </>
        )}

        {accessLevel === '3' && accessType === '3' && mdAndUp && (
          <>
            <Grid size={{ xs: 12, md: 6, lg: 4 }} display="flex">
              <MessageFailedDeliveryRoutes errorHandler={errorHandler} />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }} display="flex">
              <MessageDeliveryRates errorHandler={errorHandler} />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }} display="flex">
              <ErrorsByCode
                activeCustomerName={acting_names?.customer_name}
                errorHandler={errorHandler}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }} display="flex">
              <MessageLatency errorHandler={errorHandler} />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 4 }} display="flex">
              <ErrorsByTopCustomer errorHandler={errorHandler} />
            </Grid>
          </>
        )}
      </Grid>
    </FullscreenLayout>
  );
}
